import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { DescriptionList } from '~/eds/src/components/DescriptionList/DescriptionList';
import { MarkedText } from '~/eds/src/components/MarkedText/MarkedText';
import { Text } from '~/eds/src/components/Text/Text';
import { IconType } from '~/eds/src/types/IconType';
import { UserAction } from '~/eds/src/types/UserAction';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { IntakeForm } from '~/redux/api/methods/ticket';
import * as selectors from '~/redux/selectors';

import { useDisplayQuestionValue } from './useDisplayQuestionValue';

type Props = {
  questionIds: string[];
  description?: string;
  search?: string;
};
export const WorkflowDisplayForm = ({
  search,
  description,
  questionIds,
}: Props) => {
  const intl = useIntl();
  const isWSSIntegrationPhase2M1M3Enabled = useFlag(
    FlagType.WSSIntegrationPhase2M1M3,
  );
  const questions = useSelector((intakeForm: IntakeForm) =>
    selectors.selectQuestionsWithFields(intakeForm, questionIds),
  );
  const displayQuestionValue = useDisplayQuestionValue();

  const terms = questions
    .map((question) => {
      if (
        !question.field.isEnabled ||
        [WorkflowFieldType.DateSigned, WorkflowFieldType.Signature].includes(
          question.field.type,
        )
      ) {
        return null;
      }

      const shouldShowDatabaseIcon =
        question.isReadOnly &&
        !(isWSSIntegrationPhase2M1M3Enabled && question.field.wellKnownKey);

      return {
        title: question.title,
        tooltip: question.description ?? '',
        description: displayQuestionValue(question.field),
        actions: shouldShowDatabaseIcon
          ? [
              {
                icon: 'database' as IconType,
                mode: 'icon' as const,
                text: intl.formatMessage({
                  id: 'WorkflowDisplayForm.syncedFieldTooltip',
                  defaultMessage:
                    'This is a synced field and cannot be edited in Evisort',
                  description:
                    'Displays in a tooltip on the intake form when a question field is read-only',
                }),
              },
            ]
          : [],
      };
    })
    .filter((term) => term !== null) as {
    title: string;
    actions?: UserAction[];
    tooltip?: string;
    description: React.ReactNode;
  }[];

  return (
    <>
      {description && (
        <Text preset="description" pb={2}>
          <MarkedText search={search} text={description} />
        </Text>
      )}
      <DescriptionList
        terms={terms}
        // Setting this limit is a bit of a hack, as there's an issue right now with the component where the limit gets set to the first intake form opened.
        // If you navigate away and to another ticket page, the limits used are the text.lengths of the first ticket values instead of getting updated to the current text lengths
        limit={1000}
        search={search}
      />
    </>
  );
};
